import React, { useState } from "react";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Table from "../common/Table";
import AdGroupForm from "../forms/AdGroupForm";
import { useAdGroupsInfinite } from "../../hooks/useAdGroupsInfinite";
import {
  AdGroup,
  AdGroupFormData,
  AdGroupUpdateFormData,
  AdGroupRequest,
} from "../../schemas/adGroupSchema";

const AdGroups: React.FC = () => {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [selectedAdGroup, setSelectedAdGroup] = useState<AdGroup | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [search, setSearch] = useState("");

  const {
    adGroups,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createAdGroup,
    updateAdGroup,
    deleteAdGroups,
  } = useAdGroupsInfinite({ search });

  const handleCreate = () => {
    setSelectedAdGroup(undefined);
    setIsFormModalOpen(true);
  };

  const handleEdit = (adGroup: AdGroup) => {
    setSelectedAdGroup(adGroup);
    setIsFormModalOpen(true);
  };

  const handleDelete = async (adGroup: AdGroup) => {
    if (window.confirm(`Delete Ad Group "${adGroup.name}"?`)) {
      await deleteAdGroups([adGroup.id]);
    }
  };

  const handleBulkDelete = (ids: string[]) => {
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} ad group(s)?`,
      )
    ) {
      deleteAdGroups(ids);
      setRowSelection({});
    }
  };

  const handleSubmit = async (
    data: AdGroupFormData | AdGroupUpdateFormData,
  ) => {
    setIsSubmitting(true);
    try {
      if (selectedAdGroup?.id) {
        await updateAdGroup({
          id: selectedAdGroup.id,
          adGroup: data as AdGroupUpdateFormData,
        });
      } else {
        const payload: AdGroupRequest = {
          name: data.name || "",
          description: data.description,
        };
        await createAdGroup(payload);
      }
      setIsFormModalOpen(false);
      setSelectedAdGroup(undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsFormModalOpen(false);
    setSelectedAdGroup(undefined);
  };

  const columns: ColumnDef<AdGroup>[] = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "description", header: "Description" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PencilIcon className="h-4 w-4" />,
      onClick: handleEdit,
      variant: "success" as const,
    },
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
    },
  ];

  return (
    <>
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-xl font-bold text-gray-800">Ad Groups</h1>
        <button
          onClick={handleCreate}
          className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <Table
        columns={columns}
        data={adGroups as AdGroup[]}
        actions={actions}
        isLoading={isLoading}
        maxHeight="48vh"
        pagination
        searchable
        infinite
        hasNextPage={hasNextPage}
        isFetchingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
        onSearch={setSearch}
        selectable
        getRowId={(adGroup) => adGroup.id}
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        bulkActions={[
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]}
      />

      {isFormModalOpen && (
        <AdGroupForm
          isOpen={isFormModalOpen}
          onCancel={handleCancel}
          initialData={selectedAdGroup}
          onSubmit={handleSubmit}
          isLoading={isSubmitting}
          isUpdate={!!selectedAdGroup}
        />
      )}
    </>
  );
};

export default AdGroups;
